import { useState, useCallback } from 'react';
import { NavIcon } from '../../../shell/shared';

// ─── Types ───

interface TreatmentPlanItem {
  id: string;
  procedure: string;
  toothArea: string;
  notes: string;
}

interface Props {
  primary: string;
}

// ─── Component ───

export function TreatmentPlanSection({ primary }: Props) {
  const [items, setItems] = useState<TreatmentPlanItem[]>([]);
  const [isAdding, setIsAdding] = useState(false);
  const [draft, setDraft] = useState({ procedure: '', toothArea: '', notes: '' });

  const handleAdd = useCallback(() => {
    if (!draft.procedure.trim()) return;
    setItems((prev) => [
      ...prev,
      {
        id: crypto.randomUUID(),
        procedure: draft.procedure.trim(),
        toothArea: draft.toothArea.trim(),
        notes: draft.notes.trim(),
      },
    ]);
    setDraft({ procedure: '', toothArea: '', notes: '' });
    setIsAdding(false);
  }, [draft]);

  const handleRemove = useCallback((id: string) => {
    setItems((prev) => prev.filter((item) => item.id !== id));
  }, []);

  if (items.length === 0 && !isAdding) {
    return (
      <div style={{ padding: 'var(--sp-5) var(--sp-6)' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <span
            style={{
              fontSize: 'var(--font-size-sm)',
              color: 'var(--text-muted)',
              fontStyle: 'italic',
            }}
          >
            No treatment plan yet
          </span>
          <button
            onClick={() => setIsAdding(true)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              padding: '4px 10px',
              border: `1px solid ${primary}`,
              borderRadius: 'var(--radius-sm)',
              background: 'transparent',
              color: primary,
              fontSize: 'var(--font-size-xs)',
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            <NavIcon name="plus" /> Add item
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: 'var(--sp-4) var(--sp-6)' }}>
      {/* Table */}
      {items.length > 0 && (
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: 'var(--font-size-sm)',
            marginBottom: 'var(--sp-3)',
          }}
        >
          <thead>
            <tr
              style={{
                borderBottom: '1px solid var(--border)',
                textAlign: 'left',
              }}
            >
              <th style={TH}>Procedure / Treatment</th>
              <th style={TH}>Tooth / Area</th>
              <th style={TH}>Notes</th>
              <th style={{ ...TH, width: 36 }} />
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.id} style={{ borderBottom: '1px solid var(--border)' }}>
                <td style={TD}>{item.procedure}</td>
                <td style={TD}>{item.toothArea || '—'}</td>
                <td style={TD}>{item.notes || '—'}</td>
                <td style={TD}>
                  <button
                    onClick={() => handleRemove(item.id)}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: 'var(--text-muted)',
                      padding: 2,
                      display: 'flex',
                    }}
                    title="Remove"
                  >
                    <NavIcon name="x" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Add row form */}
      {isAdding ? (
        <div
          style={{
            display: 'flex',
            gap: 8,
            alignItems: 'flex-end',
            flexWrap: 'wrap',
          }}
        >
          <div style={{ flex: 2, minWidth: 140 }}>
            <label style={FIELD_LABEL}>Procedure / Treatment</label>
            <input
              value={draft.procedure}
              onChange={(e) => setDraft((d) => ({ ...d, procedure: e.target.value }))}
              placeholder="e.g. Composite filling"
              style={INPUT}
              autoFocus
            />
          </div>
          <div style={{ flex: 1, minWidth: 100 }}>
            <label style={FIELD_LABEL}>Tooth / Area</label>
            <input
              value={draft.toothArea}
              onChange={(e) => setDraft((d) => ({ ...d, toothArea: e.target.value }))}
              placeholder="e.g. #14"
              style={INPUT}
            />
          </div>
          <div style={{ flex: 2, minWidth: 140 }}>
            <label style={FIELD_LABEL}>Notes</label>
            <input
              value={draft.notes}
              onChange={(e) => setDraft((d) => ({ ...d, notes: e.target.value }))}
              placeholder="Optional remarks"
              style={INPUT}
            />
          </div>
          <div style={{ display: 'flex', gap: 4 }}>
            <button onClick={handleAdd} style={{ ...BTN, background: primary, color: '#fff' }}>
              Add
            </button>
            <button
              onClick={() => {
                setIsAdding(false);
                setDraft({ procedure: '', toothArea: '', notes: '' });
              }}
              style={{
                ...BTN,
                background: 'var(--bg-surface)',
                color: 'var(--text-muted)',
                border: '1px solid var(--border)',
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <button
          onClick={() => setIsAdding(true)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            padding: '4px 10px',
            border: `1px solid ${primary}`,
            borderRadius: 'var(--radius-sm)',
            background: 'transparent',
            color: primary,
            fontSize: 'var(--font-size-xs)',
            fontWeight: 500,
            cursor: 'pointer',
          }}
        >
          <NavIcon name="plus" /> Add item
        </button>
      )}
    </div>
  );
}

// ─── Styles ───

const TH: React.CSSProperties = {
  padding: '8px 8px 8px 0',
  fontSize: 'var(--font-size-xs)',
  fontWeight: 600,
  color: 'var(--text-muted)',
  textTransform: 'uppercase',
  letterSpacing: '0.3px',
};

const TD: React.CSSProperties = {
  padding: '10px 8px 10px 0',
  color: 'var(--text)',
  verticalAlign: 'top',
};

const FIELD_LABEL: React.CSSProperties = {
  display: 'block',
  fontSize: 'var(--font-size-xs)',
  color: 'var(--text-muted)',
  marginBottom: 3,
};

const INPUT: React.CSSProperties = {
  width: '100%',
  height: 30,
  padding: '0 8px',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius-sm)',
  fontSize: 'var(--font-size-sm)',
  color: 'var(--text)',
  background: 'var(--bg-surface)',
};

const BTN: React.CSSProperties = {
  height: 30,
  padding: '0 12px',
  border: 'none',
  borderRadius: 'var(--radius-sm)',
  fontSize: 'var(--font-size-xs)',
  fontWeight: 600,
  cursor: 'pointer',
};
